import { some, forEach } from 'lodash'
import Quadtree from '@timohausmann/quadtree-js'
import TimetoastTemporal from '@base/timetoast_temporal.js'
import { createLinearScale, clamp } from './math.ts'
import { rectsIntersect, rectContains } from './trig.js'

const MIN_MARKER_WIDTH = 200
const MIN_MARKER_HEIGHT = 45
const MIN_TIMESPAN_HEIGHT = 20

function collides(quadTree, targetRect) {
  if (quadTree) {
    return some(
      quadTree.retrieve(targetRect),
      function (el) {
        return rectsIntersect(el, targetRect)
      },
      this
    )
  }
  return false
}

export function padTimeline(surfaceWidth, duration, firstDate, lastDate) {
  // calculate padding
  const { leftPadding, rightPadding } = getPadding()
  // add time padding to timeline duration

  let newStartTime, newEndTime

  if (duration === 0 || duration === null) {
    //console.log("Duration was", duration, "so no need to proceed")
    const paddingTime = 30000000000 / 2
    newStartTime = firstDate.getTime() - paddingTime
    newEndTime = lastDate.getTime() + paddingTime
  } else {
    const timelineWidth = surfaceWidth - (leftPadding + rightPadding)
    const ratio = timelineWidth / surfaceWidth
    const paddedDuration = (duration / ratio) * 1

    // now that we have the padded duration, use the difference to find out how much 1 unit of padding is (in time)
    const diff = paddedDuration - duration
    const totalPadding = leftPadding + rightPadding
    const paddingTimeUnit = diff / totalPadding

    const leftPaddingTime = paddingTimeUnit * leftPadding
    const rightPaddingTime = paddingTimeUnit * rightPadding

    // and subtract/add the new time to the original start/end dates
    newStartTime = firstDate.getTime() - leftPaddingTime
    newEndTime = lastDate.getTime() + rightPaddingTime
  }

  // make sure to keep within supported range of TimetoastTemporal object
  newStartTime = clamp(newStartTime, TimetoastTemporal.MS_MIN, TimetoastTemporal.MS_MAX)
  newEndTime = clamp(newEndTime, TimetoastTemporal.MS_MIN, TimetoastTemporal.MS_MAX)
  const newDuration = newEndTime - newStartTime

  return {
    startTime: newStartTime,
    endTime: newEndTime,
    duration: newDuration,
  }
}

function getPadding() {
  const leftPadding = 10
  const rightPadding = 10 + MIN_MARKER_WIDTH + 2 // also add border for now
  return { leftPadding, rightPadding }
}

// does heavy lifting of timeline item placement
// we create a large area in which to place items and start at the top
export function layoutTimelineItems(dimensions, timelineItemScalePositions, breakOutIfNoFit) {
  //const consoleItemsTotal = timelineItemScalePositions.timespans.length + timelineItemScalePositions.markers.length
  //console.log(`Laying out ${consoleItemsTotal} items on a ${dimensions.width}x${dimensions.height} canvas.`)

  // first declare some variables to store our result
  let markersResult = {}
  let timespansResult = {}
  let outOfBounds = false

  // these variables are used to track the actual occupied dimensions of timeline items
  let topY = 0

  // the item margin is the margin around each item margin.
  const itemMargin = 5
  const doubledItemMargin = itemMargin * 2

  const quadTreeHeight = 1000000

  // Note: To check if an item would be out of the requested bounds, we must adapt its size to include the item margin and place it at the bottom of the quad tree
  const height = dimensions.height + doubledItemMargin
  const bounds = {
    x: dimensions.x - itemMargin,
    y: quadTreeHeight - (height - itemMargin),
    width: dimensions.width + doubledItemMargin,
    height: height,
  }
  const quadTreeDimensions = {
    x: dimensions.x,
    y: dimensions.y,
    width: dimensions.width,
    height: quadTreeHeight,
  }
  const verticalOffset = quadTreeHeight - dimensions.height
  // create a dimensions object for the quadtree that has much more height
  let quadTree = new Quadtree(quadTreeDimensions)
  const scale = createLinearScale([0, 1], [0, dimensions.width])

  // this is the maximum vertical space a timeline item can fill. If we find a free spot, we centre the timeline item within it
  let slotHeight = MIN_TIMESPAN_HEIGHT + doubledItemMargin

  // start with timespans
  const timespans = timelineItemScalePositions.timespans

  let attempts = 0
  let target
  forEach(timespans, item => {
    target = {
      x: scale(item.scalePosition) - itemMargin,
      y: quadTreeHeight - MIN_TIMESPAN_HEIGHT,
      width: scale(item.scaleWidth) + doubledItemMargin,
      height: MIN_TIMESPAN_HEIGHT,
    }
    attempts = 0
    while (collides(quadTree, target) && attempts < 100) {
      target.y -= 5
      if (!rectContains(bounds, target)) {
        outOfBounds = true
        //console.log("Timespan target y ", target.y, "was out of bounds, it was less than", bounds.y)
        if (breakOutIfNoFit) {
          //console.log("Timespan target y ", target.y, "was out of bounds, it was less than", bounds.y, "oh, we should break and return now")
          return { outOfBounds: true }
        }
      }
      attempts += 1
    }
    // found a spot, or hit max attempts
    target.itemType = 'timespan'
    target.itemId = item.id
    quadTree.insert(target)
    // target inserted into quadtree includes padding and a much larger y position, so calculate the actual position here
    const actualTarget = {
      x: target.x + itemMargin,
      y: target.y + slotHeight / 2 - target.height / 2 - verticalOffset,
      width: target.width - doubledItemMargin,
      height: MIN_TIMESPAN_HEIGHT, // default timespan height
    }
    // update the topY if needed
    const actualY = actualTarget.y
    if (actualY < topY) {
      topY = actualY
    }
    const itemIdString = item.id.toString()
    timespansResult[itemIdString] = actualTarget
  })

  // OK, now place the markers
  const markers = timelineItemScalePositions.markers

  const slotWidth = MIN_MARKER_WIDTH + doubledItemMargin
  slotHeight = MIN_MARKER_HEIGHT + doubledItemMargin

  forEach(markers, item => {
    target = {
      x: scale(item.scalePosition) - itemMargin,
      y: quadTreeHeight - MIN_MARKER_HEIGHT,
      width: slotWidth,
      height: slotHeight,
    }
    attempts = 0
    while (collides(quadTree, target) && attempts < 400) {
      target.y -= 5
      if (!rectContains(bounds, target)) {
        outOfBounds = true
        //console.log("Marker target y ", target.y, "was out of bounds, it was less than", bounds.y)
        if (breakOutIfNoFit) {
          //console.log("Target Y of ", target.y, "was out of bounds, it was less than", dimensions.y, "oh, and we should break here")
          return { outOfBounds: true }
        }
      }
      attempts += 1
    }
    // found a spot, or hit max attempts
    target.itemType = 'marker'
    target.itemId = item.id
    quadTree.insert(target)
    // target inserted into quadtree includes padding and a much larger y position, so calculate the actual position here
    const actualTarget = {
      x: target.x + itemMargin,
      y: target.y + itemMargin - verticalOffset,
      width: MIN_MARKER_WIDTH,
      height: MIN_MARKER_HEIGHT,
    }
    // update the topY if needed
    const actualY = actualTarget.y
    if (actualY < topY) {
      topY = actualY
    }
    const itemIdString = item.id.toString()
    markersResult[itemIdString] = actualTarget
  })
  return {
    markers: markersResult,
    timespans: timespansResult,
    outOfBounds,
    topY,
  }
}
